import React, { useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { constants, selectors, verifiers } from '@yola/subscription-manager-js';
import i18next from 'i18next';
import redirectToUrl from 'src/js/utils/redirect-to-url';
import user from '../../user';
import dialogs from '../../dialogs';
import config from '../../config';
import segment from '../../analytics/segment';
import { DEFAULT_INITIATOR } from '../../common/constants';
import useDomainsExtraPrice from '../hooks/use-domains-extra-price';
import convertToValidDomainName from '../helpers/convert-to-valid-domain-name';
import DomainSelector from '../components/domain-selector';
import utils from '../../webapp-plugins/utils';
import actionIds from '../constants/action-ids';
import extraPrices from '../constants/extra-price-types';
import useFeatureFlags from '../../feature-flags/hooks/use-feature-flags';
import useAdaptiveViewport from '../../common/hooks/use-adaptive-viewport';
import useHideZendeskOnTouchDevice from '../../help-center/zendesk/hooks/use-hide-zendesk-on-touch-device';
import isSpecialOfferAvailable from '../helpers/is-special-offer-available';

const {
  constants: { events },
  track,
  trackAsync,
} = segment;

const {
  hostingPackageFeatureNames,
  SubscriptionTerm: { MONTHLY, ANNUAL },
} = constants;

const defaultExtraProducts = [];

function DomainSelectorContainer({
  initiator = '',
  prefill = '',
  extraProducts = defaultExtraProducts,
  term = '',
  triggerId = null,
  showPaywall,
  paymentBackUrl: overridePaymentBackUrl = '',
  promoCampaignId = '',
}) {
  const baseUrl = useSelector(config.selectors.getMyyolaUrl);
  const { currency } = useSelector(user.selectors.getUserPreferences);
  const isB2C = useSelector(user.selectors.getIsB2C);
  const currentPlan = useSelector(selectors.getCurrentPackage, shallowEqual);
  const packageType = useSelector(selectors.getCurrentPackageType);
  const activeHostingSubscription = useSelector(selectors.getActiveHostingSubscription);
  const activeSubscriptions = useSelector(selectors.getActiveSubscriptions);
  const subscriptionsList = useSelector(selectors.getSubscriptionList, shallowEqual);

  const dispatch = useDispatch();
  const onBackButtonClick = useCallback(() => {
    window.history.back();
  }, []);

  const hasExtraProducts = Boolean(extraProducts.length);
  const isPaidHostingProduct =
    hasExtraProducts && verifiers.hasPaidHostingPackageType(extraProducts);
  const isCustomDomainFeatureAvailable =
    currentPlan?.features?.[hostingPackageFeatureNames.CUSTOM_DOMAIN];
  const isSubscriptionExpired = verifiers.hasExpiredPaidSubscription(activeSubscriptions);
  const paymentBackUrl = overridePaymentBackUrl || baseUrl;
  const bundledSubscriptionType = extraProducts.find(verifiers.isPaidHostingPackageType);

  const captions = useMemo(
    () => ({
      closeButton: i18next.t('Back'),
    }),
    []
  );

  const additionalPaymentParams = useMemo(
    () => ({
      initiator: initiator || DEFAULT_INITIATOR,
      ...(hasExtraProducts && { products: extraProducts }),
      ...(term && { term }),
      p1y_upgrade: term === ANNUAL && activeHostingSubscription?.term === MONTHLY,
      backUrl: paymentBackUrl,
      successUrl: paymentBackUrl,
      failUrl: paymentBackUrl,
      closeUrl: paymentBackUrl,
      promo_campaign_id: promoCampaignId,
    }),
    [
      initiator,
      hasExtraProducts,
      extraProducts,
      term,
      paymentBackUrl,
      activeHostingSubscription,
      promoCampaignId,
    ]
  );

  const [featureFlags] = useFeatureFlags(['freedomain', 'free_trial_upsell']);

  const trackingData = {
    siteId: null,
    templateBuildSlug: null,
    ...(triggerId && { triggerId }),
  };
  const { free_trial_upsell: isFreeTrialUpsellEnabled } = featureFlags;

  const subscriptionsTypes = subscriptionsList.map(({ type }) => type);
  const hasEverHadPaidHostingSubscription = verifiers.hasPaidHostingPackageType(subscriptionsTypes);
  const isFreeTrialAvailable = isFreeTrialUpsellEnabled && !hasEverHadPaidHostingSubscription;
  const extraPriceType = useDomainsExtraPrice(isFreeTrialAvailable);

  const isActiveTrialSubscription =
    isFreeTrialUpsellEnabled &&
    activeHostingSubscription &&
    verifiers.isTrialSubscription(activeHostingSubscription);
  const isTrialDiscountAvailable =
    extraPriceType === extraPrices.ANNUAL_TRIAL_PACKAGE ||
    (isActiveTrialSubscription && extraPriceType === extraPrices.NO_DISCOUNTS);

  useEffect(() => {
    track(events.DOMAIN_PURCHASE_DIALOG_DISPLAYED, trackingData);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const handleSelect = async ({ paymentUrl, domain, searchString, shouldBundleSubscription }) => {
    const indexOfDomainTypeSeparator = domain.indexOf('.');
    const domainNameSelected = domain.substring(0, indexOfDomainTypeSeparator);
    const domainTypeSelected = domain.substring(indexOfDomainTypeSeparator + 1);

    await trackAsync(events.DOMAIN_PURCHASE_CLICKED, {
      ...trackingData,
      domainName: searchString,
      domainNameSelected,
      domainTypeSelected,
      actionId: actionIds.BUY_DOMAIN,
      activeHostingSubscription: packageType,
    });

    if (shouldBundleSubscription) {
      showPaywall(domain);
      return;
    }

    if (isPaidHostingProduct || isCustomDomainFeatureAvailable || isSubscriptionExpired) {
      dispatch(dialogs.actions.show(dialogs.dialogTypes.PENDING));
      redirectToUrl(paymentUrl);
      return;
    }

    showPaywall(domain);
  };

  const handleDomainSearchFailure = () => {
    track(events.DOMAIN_SEARCH_FAILED, trackingData);
    dispatch(dialogs.actions.show(dialogs.dialogTypes.DOMAIN_SEARCH_FAILURE));
  };

  const handleClose = async () => {
    await trackAsync(events.DOMAIN_PURCHASE_DIALOG_CANCELED, trackingData);
    dispatch(dialogs.actions.hide());
    onBackButtonClick();
  };

  const handleNext = async () => {
    dispatch(dialogs.actions.show(dialogs.dialogTypes.PENDING));
    await trackAsync(events.DOMAIN_PURCHASE_NOT_NOW_CLICKED, trackingData);
    redirectToUrl(utils.generatePaymentUrl(additionalPaymentParams, isB2C));
  };

  const handleSearchStart = ({ searchString, domainsRequestOptions }) => {
    if (domainsRequestOptions && domainsRequestOptions.userAction) {
      track(events.DOMAIN_PURCHASE_SEARCH_CLICKED, {
        ...trackingData,
        domainName: searchString,
      });
    }
  };

  const handleSearchPerformed = ({ searchString, searchResults, domainsRequestOptions }) => {
    const availableDomains = searchResults.filter(({ available }) => available);
    const isSearchedDomainAvailable = availableDomains.some(({ domain }) => {
      const domainName = domain.substring(0, domain.indexOf('.'));
      return searchString === domain || searchString === domainName;
    });
    const suggestedDomainsAvailable = availableDomains.length;
    const suggestedDomainsTaken = searchResults.length - availableDomains.length;

    track(events.DOMAIN_PURCHASE_SEARCH_PERFORMED, {
      ...trackingData,
      domainName: searchString,
      isSearchedDomainAvailable,
      suggestedDomainsAvailable,
      suggestedDomainsTaken,
      searchClicked: Boolean(domainsRequestOptions && domainsRequestOptions.userAction),
    });
  };

  const handleDomainFieldChange = (option) => {
    if (option && option.tld) {
      track(events.DOMAIN_PURCHASE_AUTOCOMPLETE_DOMAIN_SELECTED, {
        ...trackingData,
        domainName: option.value,
        tld: option.tld,
      });
    }
  };

  useAdaptiveViewport();
  useHideZendeskOnTouchDevice();

  return (
    <DomainSelector
      domainName={convertToValidDomainName(prefill)}
      currency={currency}
      captionsOverrides={captions}
      onSelect={handleSelect}
      onDomainSearchFailure={handleDomainSearchFailure}
      onClose={handleClose}
      onNext={isPaidHostingProduct ? handleNext : null}
      onSearchStart={handleSearchStart}
      onSearchPerformed={handleSearchPerformed}
      onDomainFieldChange={handleDomainFieldChange}
      additionalPaymentParams={additionalPaymentParams}
      isSpecialOfferAvailable={isSpecialOfferAvailable({
        bundledSubscriptionType,
        extraPriceType,
        activeHostingSubscription,
      })}
      extraPrice={extraPriceType}
      activeSubscriptionTerm={activeHostingSubscription?.term}
      isTrialDiscountAvailable={isTrialDiscountAvailable}
    />
  );
}

DomainSelectorContainer.propTypes = {
  initiator: PropTypes.string,
  prefill: PropTypes.string,
  extraProducts: PropTypes.arrayOf(PropTypes.string),
  term: PropTypes.string,
  triggerId: PropTypes.string,
  showPaywall: PropTypes.func.isRequired,
  paymentBackUrl: PropTypes.string,
  promoCampaignId: PropTypes.string,
};

export default DomainSelectorContainer;
